import { Link } from "wouter";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Seo } from "@/components/Seo";
import { Section, Eyebrow } from "@/components/Section";
import { AnimatedNumber } from "@/components/AnimatedNumber";
import { EncryptedShieldPanel } from "@/components/EncryptedShieldPanel";
import { AustraliaCallout } from "@/components/AustraliaCallout";
import { APP_SIGN_UP_URL, trackEvent } from "@/lib/utils";
import type { Industry } from "@/lib/industries";
import { INDUSTRIES } from "@/lib/industries";

export function IndustryPage({ industry, path }: { industry: Industry; path: string }) {
  return (
    <>
      <Seo title={industry.seoTitle} description={industry.seoDescription} path={path} />

      {/* Hero */}
      <Section className="!pt-8 sm:!pt-24 !pb-12">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{industry.heroEyebrow}</Eyebrow>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-[hsl(var(--brand-charcoal))]">
              {industry.heroTitle}{" "}
              <span className="text-[hsl(var(--brand-forest))]">{industry.heroTitleAccent}</span>
            </h1>
            <p className="mt-6 text-lg text-[hsl(var(--brand-slate))] max-w-xl leading-relaxed">
              {industry.heroSub}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={`${APP_SIGN_UP_URL}?industry=${industry.slug}`}
                onClick={() => trackEvent("industry_hero_signup", { industry: industry.slug })}
                className="inline-flex items-center gap-2 bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-6 py-3 rounded font-medium hover:bg-[hsl(168,25%,22%)] transition-colors"
                data-testid={`button-industry-${industry.slug}-signup`}
              >
                Create your workspace <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                href="/how-it-works"
                className="inline-flex items-center gap-2 border border-[hsl(var(--brand-sand))] bg-white/40 text-[hsl(var(--brand-charcoal))] px-6 py-3 rounded font-medium hover:bg-white/70 transition-colors"
              >
                See how it works
              </Link>
            </div>
            <div className="mt-8 font-mono text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
              {industry.trustStripLine}
            </div>
          </div>
          <div className="lg:col-span-5">
            <EncryptedShieldPanel />
          </div>
        </div>
      </Section>

      {/* Pain block */}
      <Section className="!py-16 border-t border-[hsl(var(--brand-sand))]">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <Eyebrow>{industry.painEyebrow}</Eyebrow>
            <h2 className="mt-3 text-3xl font-semibold text-[hsl(var(--brand-charcoal))]">
              {industry.painTitle}
            </h2>
          </div>
          <div className="md:col-span-2 space-y-5 text-[hsl(var(--brand-slate))] leading-relaxed">
            <p>{industry.painBody}</p>
            <p>
              FortifyID fixes the chain of custody: a verified identity, a typed submission, and a
              timestamped event log on every change.
            </p>
          </div>
        </div>
      </Section>

      {/* Scenarios */}
      <Section className="!pt-0">
        <Eyebrow>{industry.scenariosTitle}</Eyebrow>
        <div className="mt-8 grid sm:grid-cols-2 gap-px bg-[hsl(var(--brand-sand))] border border-[hsl(var(--brand-sand))] rounded-xl overflow-hidden">
          {industry.scenarios.map((s) => (
            <div key={s.title} className="bg-[hsl(var(--brand-bone))] p-6 sm:p-8">
              <div className="w-10 h-10 rounded bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-brass))] flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-[hsl(var(--brand-charcoal))]">
                {s.title}
              </h3>
              <p className="mt-2 text-[hsl(var(--brand-slate))] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Steps */}
      <Section className="!pt-0">
        <div className="rounded-2xl border border-[hsl(var(--brand-sand))] bg-white/40 p-6 sm:p-10">
          <Eyebrow>How a request flows</Eyebrow>
          <div className="mt-8 grid md:grid-cols-4 gap-8">
            {industry.steps.map((s) => (
              <div key={s.n}>
                <div className="font-mono text-xs text-[hsl(var(--brand-brass))]">{s.n}</div>
                <div className="mt-2 font-semibold text-[hsl(var(--brand-charcoal))]">{s.t}</div>
                <p className="mt-2 text-sm text-[hsl(var(--brand-slate))] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Proof stat */}
      <Section className="!pt-0">
        <div
          className="rounded-xl border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] p-5 sm:p-6 grid sm:grid-cols-[auto,1fr] gap-5 items-center"
          data-testid={`callout-stat-${industry.slug}`}
        >
          <AnimatedNumber
            value={industry.proof.value}
            className="block text-4xl sm:text-5xl font-semibold text-[hsl(var(--brand-forest))] leading-none"
          />
          <div className="text-sm text-[hsl(var(--brand-charcoal))] leading-relaxed">
            {industry.proof.label}
            <div className="mt-2 font-mono text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--brand-slate))]">
              Source: {industry.proof.source}
            </div>
          </div>
        </div>
      </Section>

      {/* AU sovereignty */}
      <Section className="!pt-0">
        <AustraliaCallout />
      </Section>

      {/* FAQ */}
      <Section className="!pt-0">
        <Eyebrow>Frequently asked</Eyebrow>
        <IndustryFaqs faqs={industry.faqs} slug={industry.slug} />
      </Section>

      {/* Cross-link to other industries */}
      <Section className="!pt-0">
        <div className="border-t border-[hsl(var(--brand-sand))] pt-10">
          <Eyebrow>Also for</Eyebrow>
          <div className="mt-4 flex flex-wrap gap-2">
            {INDUSTRIES.filter((i) => i.slug !== industry.slug).map((i) => (
              <Link
                key={i.slug}
                href={`/industries/${i.slug}`}
                className="text-sm px-3 py-1.5 rounded-full border border-[hsl(var(--brand-sand))] bg-white/50 text-[hsl(var(--brand-charcoal))] hover:bg-[hsl(var(--brand-bone))] transition-colors"
                data-testid={`link-related-${i.slug}`}
              >
                {i.name}
              </Link>
            ))}
            <Link
              href="/industries"
              className="text-sm px-3 py-1.5 rounded-full border border-[hsl(var(--brand-forest))] text-[hsl(var(--brand-forest))] hover:bg-[hsl(var(--brand-forest))] hover:text-[hsl(var(--brand-bone))] transition-colors"
            >
              All industries →
            </Link>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <div className="rounded-2xl bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] p-8 sm:p-12 md:p-16 grid md:grid-cols-3 gap-10 items-center">
          <div className="md:col-span-2">
            <Eyebrow>
              <span className="text-[hsl(var(--brand-brass))]">Ready to start</span>
            </Eyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold leading-tight">
              {industry.ctaTitle}
            </h2>
            <p className="mt-4 text-[hsl(var(--brand-sand))] max-w-xl">{industry.ctaBody}</p>
          </div>
          <div className="flex flex-col gap-3">
            <a
              href={`${APP_SIGN_UP_URL}?industry=${industry.slug}`}
              onClick={() => trackEvent("industry_cta_signup", { industry: industry.slug })}
              className="text-center bg-[hsl(var(--brand-brass))] text-[hsl(var(--brand-charcoal))] px-6 py-3 rounded font-medium hover:bg-[hsl(42,55%,52%)] transition-colors"
              data-testid={`button-industry-${industry.slug}-cta`}
            >
              Create your workspace
            </a>
            <Link
              href="/contact"
              className="text-center border border-[hsl(var(--brand-sand))]/30 px-6 py-3 rounded font-medium hover:bg-white/5 transition-colors"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}

function IndustryFaqs({ faqs, slug }: { faqs: { q: string; a: string }[]; slug: string }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="mt-6 rounded-xl border border-[hsl(var(--brand-sand))] bg-white/50 divide-y divide-[hsl(var(--brand-sand))]">
      {faqs.map((item, i) => {
        const isOpen = open === i;
        return (
          <div key={item.q}>
            {/* Desktop: always expanded, no chevron */}
            <div className="hidden md:block px-5 py-5">
              <div className="font-medium text-[hsl(var(--brand-charcoal))]">{item.q}</div>
              <div
                className="mt-2 text-sm text-[hsl(var(--brand-slate))] leading-relaxed"
                data-testid={`faq-${slug}-content-${i}`}
              >
                {item.a}
              </div>
            </div>

            {/* Mobile: accordion with chevron */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-[hsl(var(--brand-bone))] transition-colors"
                data-testid={`faq-${slug}-trigger-${i}`}
              >
                <span className="font-medium text-[hsl(var(--brand-charcoal))]">{item.q}</span>
                <ChevronDown
                  className={
                    "w-4 h-4 shrink-0 text-[hsl(var(--brand-slate))] transition-transform " +
                    (isOpen ? "rotate-180" : "")
                  }
                />
              </button>
              {isOpen && (
                <div
                  className="px-5 pb-5 text-sm text-[hsl(var(--brand-slate))] leading-relaxed"
                  data-testid={`faq-${slug}-content-${i}`}
                >
                  {item.a}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
